import DragHandle from '@tiptap-pro/extension-drag-handle-react';
import { Editor } from '@tiptap/react';
import {
  Popover,
  IconButton,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Box,
} from '@mui/material';
import AddIcon from '@material-ui/icons/Add';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import FormatClearIcon from '@material-ui/icons/FormatClear';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import DeleteIcon from '@material-ui/icons/Delete';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useContentItemActions from '../hooks/useContentItemsActions';
import useData from '../hooks/useData';

const ContentItemMenu = ({ editor, isEditable = true }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const data = useData();
  const actions = useContentItemActions(editor, data.currentNode, data.currentNodePos);

  useEffect(() => {
    if (editor) editor.commands.setMeta('lockDragHandle', Boolean(menuAnchorEl));
  }, [editor, menuAnchorEl]);

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const menuOpen = Boolean(menuAnchorEl);
  const options = [
    {
      title: '',
      items: [
        { label: 'Clear formatting', icon: <FormatClearIcon fontSize="small" />, onClick: actions.resetTextFormatting },
        { label: 'Copy to clipboard', icon: <FileCopyIcon fontSize="small" />, onClick: actions.copyNodeToClipboard },
      ],
    },
    {
      title: '',
      items: [
        {
          label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: actions.deleteNode, danger: true,
        },
      ],
    },
  ];
  return (
    <DragHandle
      pluginKey="ContentItemMenu"
      editor={editor}
      className="modal-safe-click-zone"
      onNodeChange={data.handleNodeChange}
      tippyOptions={{
        offset: [0, 10],
        delay: [0, 100],
        zIndex: 9999,
      }}
    >
      {isEditable && (
        <div className="flex items-center gap-1 modal-safe-click-zone">
          <IconButton size="small" onClick={actions.handleAdd}>
            <AddIcon fontSize="small" />
          </IconButton>

          <IconButton size="small" onClick={handleMenuOpen}>
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Popover
            className="modal-safe-click-zone"
            open={menuOpen}
            anchorEl={menuAnchorEl}
            onClose={handleMenuClose}
            container={menuAnchorEl?.ownerDocument?.body}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{
              className: 'modal-safe-click-zone',
              onMouseDown: (e) => e.stopPropagation(),
              onClick: (e) => e.stopPropagation(),
              sx: { display: 'flex', flexDirection: 'column', p: 0.5 },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {options.map((section, sectionIdx) => (
                <Box key={`${section} ${sectionIdx.toString()}`} sx={{ display: 'flex', flexDirection: 'column' }}>
                  {section.title && (
                  <Typography
                    variant="caption"
                    sx={{
                      px: 2, pt: sectionIdx !== 0 ? 1 : 0.5, pb: 0.5, color: 'text.secondary', fontWeight: 500,
                    }}
                  >
                    {section.title}
                  </Typography>
                  )}

                  {section.items.map(({
                    label, icon, onClick, danger,
                  }) => (
                    <MenuItem
                      key={label}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuClose();
                        onClick();
                      }}
                      sx={{ color: danger ? 'error.main' : 'inherit' }}
                    >
                      {icon && <ListItemIcon>{icon}</ListItemIcon>}
                      <ListItemText>{label}</ListItemText>
                    </MenuItem>
                  ))}

                  {sectionIdx !== options.length - 1 && <Divider />}
                </Box>
              ))}
            </Box>
          </Popover>
        </div>
      )}
    </DragHandle>
  );
};
ContentItemMenu.propTypes = {
  editor: PropTypes.instanceOf(Editor).isRequired,
  isEditable: PropTypes.bool,
};
ContentItemMenu.defaultProps = {
  isEditable: true,
};
export default ContentItemMenu;
